import { LoomSiteAri } from '@atlassian/ari';
import { ConfluenceSiteAri } from '@atlassian/ari/confluence/site';

import { useAppsByProduct, useIsLoadingAppsStore } from '@townsquare/apps-store/hooks';
import { SwitcherProductLink } from '@townsquare/apps-store/types';
import { useCollabContext, useRovoWorkspace, useUnitsEnabled } from '@townsquare/collab-context';
import { WORKLENS_PRODUCT_TYPE } from '@townsquare/product-type';

const checkCurrentSiteAccess = (
	permittedConfluenceSites: SwitcherProductLink[],
	permittedLoomSites: SwitcherProductLink[],
	cloudId: string | null | undefined,
) => {
	const hasConfluence = permittedConfluenceSites.some(site => site.cloudId === cloudId);
	const hasLoom = permittedLoomSites.some(site => site.cloudId === cloudId);
	return { hasConfluence, hasLoom, hasDiscoverInCurrentSite: hasConfluence || hasLoom };
};

export const useIsDiscoverEnabled = () => {
	const cloudId = useRovoWorkspace();
	const collabContext = useCollabContext();
	const permittedProducts = useAppsByProduct();
	const isLoadingAppsStore = useIsLoadingAppsStore();
	const unitsEnabled = useUnitsEnabled();

	const permittedConfluenceSites = permittedProducts[WORKLENS_PRODUCT_TYPE.CONFLUENCE] ?? [];
	const permittedLoomSites = permittedProducts[WORKLENS_PRODUCT_TYPE.LOOM] ?? [];
	const collabContextSiteAris = collabContext?.collabContextScoping?.collabContextSiteAris ?? [];
	const collabContextSiteCloudIds = collabContext?.collabContextScoping?.collabContextCloudIds ?? [];

	// Unit support enabled: Discover is enabled if the user has access to Confluence or Loom in the current site or unit
	if (unitsEnabled) {
		const { hasConfluence, hasLoom, hasDiscoverInCurrentSite } = checkCurrentSiteAccess(
			permittedConfluenceSites,
			permittedLoomSites,
			cloudId,
		);

		// Check that there is an instance of Loom or Confluence in the current unit
		const loomOrConfluenceExistsInUnit = collabContextSiteAris.some(
			ari => ConfluenceSiteAri.check(ari) || LoomSiteAri.check(ari),
		);
		// and that the current user has access to Confluence or Loom in the unit
		const discoverCloudIdInUnit =
			permittedConfluenceSites.find(site => collabContextSiteCloudIds.includes(site.cloudId)) ||
			permittedLoomSites.find(site => collabContextSiteCloudIds.includes(site.cloudId));

		const hasDiscoverInUnit = loomOrConfluenceExistsInUnit && !!discoverCloudIdInUnit;
		const isEnabled = hasDiscoverInCurrentSite || hasDiscoverInUnit;

		return {
			hasLoaded: !isLoadingAppsStore,
			isEnabled,
			hasConfluence,
			hasLoom,
			discoverCloudId: hasDiscoverInCurrentSite ? cloudId : discoverCloudIdInUnit?.cloudId,
		};
	}

	// Default: Discover is enabled if the user has access to Confluence or Loom in the current site
	const { hasConfluence, hasLoom, hasDiscoverInCurrentSite } = checkCurrentSiteAccess(
		permittedConfluenceSites,
		permittedLoomSites,
		cloudId,
	);

	const isEnabled = hasDiscoverInCurrentSite;

	return {
		hasLoaded: !isLoadingAppsStore,
		isEnabled,
		hasConfluence,
		hasLoom,
		discoverCloudId: isEnabled ? cloudId : undefined,
	};
};
